/**
 * Figma primitive palette — faithful transcription of the Figma "default" mode export
 * (default.tokens.json, COMPONENTS → Colors + border/radius/numbers).
 *
 * This is the *incoming* source of truth (INIT: Figma palette migration). It mirrors the Figma
 * variable groups 1:1 so the two cannot drift:
 *   Figma `colors/dark-blue/*`  → `figma.darkBlue[*]`
 *   Figma `colors/neutral-solid`→ `figma.neutralSolid[*]`
 *   Figma `colors/neutral-opacity` → `figma.neutralOpacity[*]` (key = opacity %, `0` = solid base)
 *   Figma `radius|border|numbers`→ `figma.radius|border|numbers`
 *
 * Stage A (now): this module exists and is visualised in Foundations/Colors; the app still runs on
 * the legacy `primitives.ts`. Stage B rewires `semantics.ts` + `mui.theme.tsx` onto this and deletes
 * the legacy ramp. Until then it is intentionally NOT re-exported from `tokens/index.ts`.
 *
 * Values are copied verbatim from the export's `hex` / `alpha` fields. Alpha tokens are written as
 * `rgba()` so they are usable directly; solids as hex.
 */
export const figma = {
  /** Pure surfaces. `white` is Figma `white/100%`; `black` (#000000) has no Figma variable but is
   *  needed by a few shadows/scrims. */
  common: {
    white: '#ffffff',
    black: '#000000',
  },

  /** Figma `colors/grey/*` — the text-ink ramp (role names, not steps). */
  grey: {
    primary: '#2C3A42',
    secondary: '#414E55',
    tertiary: '#6B757B',
    placeholder: '#899499',
    disabled: '#B0B5B8',
  },

  /** Figma `colors/neutral-solid/*` — the neutral surface/border ramp. 0 = darkest, 120 = lightest. */
  neutralSolid: {
    0: '#00343C',
    20: '#1A5159',
    40: '#33656C',
    50: '#4E797F',
    60: '#6A8D93',
    70: '#85A2A6',
    80: '#A0B7BA',
    90: '#D6E0E2',
    100: '#EAF0F1',
    120: '#F8F8FA',
  },

  /** Figma `colors/neutral-opacity/*` — #00343C at decreasing opacity. Key = opacity %; `0` = solid. */
  neutralOpacity: {
    0: '#00343C',
    90: 'rgba(0, 52, 60, 0.9)',
    80: 'rgba(0, 52, 60, 0.8)',
    70: 'rgba(0, 52, 60, 0.7)',
    60: 'rgba(0, 52, 60, 0.6)',
    50: 'rgba(0, 52, 60, 0.5)',
    40: 'rgba(0, 52, 60, 0.4)',
    30: 'rgba(0, 52, 60, 0.3)',
    20: 'rgba(0, 52, 60, 0.2)',
    10: 'rgba(0, 52, 60, 0.1)',
    5: 'rgba(0, 52, 60, 0.05)',
  },

  /** Figma `colors/brand/*` — the teal house accent. `brand[100]` (#49BACA) is the primary. */
  brand: {
    10: '#EFF9FA',
    20: '#DCEFF2',
    35: '#C3E4EA',
    50: '#A6D9E1',
    65: '#88CED9',
    80: '#69C4D2',
    100: '#49BACA',
    125: '#1B8B99',
    150: '#055E68',
    175: '#003239',
  },

  /** Figma `colors/red/*`. `red[100]` (#FF5454) is the error anchor. */
  red: {
    10: '#FFF4F3',
    20: '#FFE0DD',
    35: '#FFC8C3',
    50: '#FFAEA8',
    65: '#FF928B',
    80: '#FF7671',
    100: '#FF5454',
    125: '#CA2F34',
    150: '#8E1B20',
    175: '#540B0E',
  },

  /** Figma `colors/orange/*`. `orange[100]` (#F17F16). */
  orange: {
    10: '#FFF4EE',
    20: '#FDE4D5',
    35: '#FBD1B6',
    50: '#FABC94',
    65: '#F8A76F',
    80: '#F69248',
    100: '#F17F16',
    125: '#B65C00',
    150: '#7D3C00',
    175: '#471E00',
  },

  /** Figma `colors/yellow/*`. `yellow[100]` (#FCC419). */
  yellow: {
    10: '#FCF6EA',
    20: '#FAF0D7',
    35: '#FAE8BD',
    50: '#FBDF9E',
    65: '#FCD57A',
    80: '#FDCD51',
    100: '#FCC419',
    125: '#C68400',
    150: '#874E00',
    175: '#452000',
  },

  /** Figma `colors/green/*`. `green[100]` (#569720) is the success anchor. */
  green: {
    10: '#F2F9EF',
    20: '#DCEAD4',
    35: '#C1D9B4',
    50: '#A5C891',
    65: '#88B66C',
    80: '#6EA748',
    100: '#569720',
    125: '#3C7500',
    150: '#295400',
    175: '#183302',
  },

  /** Figma `colors/dark-blue/*`. `darkBlue[100]` (#2B5888). */
  darkBlue: {
    10: '#E0EFFF',
    20: '#CFE6FF',
    35: '#B1C2D5',
    50: '#8DA5C1',
    65: '#6989AD',
    80: '#49709B',
    100: '#2B5888',
    125: '#204974',
    150: '#1D3B5B',
    175: '#192D42',
  },

  /** Figma `colors/pink/*`. */
  pink: {
    10: '#F9E5FF',
    20: '#F6D6FF',
    50: '#ECB4FC',
    100: '#DA6BF8',
    125: '#A844C2',
    150: '#742A87',
    175: '#41124D',
  },

  /** Figma `colors/purple/*` (hidden-from-publishing in Figma, but present). */
  purple: {
    10: '#EAEBFF',
    20: '#DFE0FF',
    50: '#908FD5',
    100: '#3F1DA4',
    125: '#371992',
    150: '#2F1F76',
    175: '#272159',
  },

  /** Figma `colors/white/*` — white at decreasing opacity. Key = opacity %. */
  white: {
    100: '#ffffff',
    80: 'rgba(255, 255, 255, 0.8)',
    50: 'rgba(255, 255, 255, 0.5)',
    20: 'rgba(255, 255, 255, 0.2)',
    10: 'rgba(255, 255, 255, 0.1)',
    5: 'rgba(255, 255, 255, 0.05)',
  },

  /** Figma `section/*` — dark scrim overlays (modal/section backgrounds). */
  section: {
    bg: 'rgba(26, 26, 26, 0.4)',
    bg2: 'rgba(26, 26, 26, 0.1)',
    bg3: 'rgba(0, 0, 0, 0.6)',
  },

  /** Figma `radius/*` — corner radii (px). `full` (1000) = pill. */
  radius: {
    none: 0,
    4: 4,
    8: 8,
    12: 12,
    16: 16,
    full: 1000,
  },

  /** Figma `border/*` — stroke widths (px). */
  border: {
    half: 0.5,
    default: 1,
    strong: 2,
  },

  /** Figma `numbers/*` — the raw spacing / sizing scale (px). */
  numbers: {
    0: 0,
    0.5: 0.5,
    1: 1,
    2: 2,
    4: 4,
    8: 8,
    12: 12,
    14: 14,
    16: 16,
    24: 24,
    32: 32,
    40: 40,
    48: 48,
    56: 56,
    64: 64,
    72: 72,
    80: 80,
    100: 100,
    1000: 1000,
  },
} as const;
